import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, DollarSign, FileText, AlertCircle } from 'lucide-react';
export function ProblemSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const mistakes = ['No pide actualizaciones a tiempo', 'No detecta fees duplicados', 'No confirma seguro/taxes/HOA', "Llega a 72 horas del cierre 'rezando' que el número no cambie"];
  return <section className="py-24 bg-cream-100 text-navy-900" data-id="element-170">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-171">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="space-y-12" data-id="element-172">
          {/* Opening Lead */}
          <div className="prose prose-lg prose-headings:font-serif prose-headings:text-navy-900 prose-p:text-navy-800/80 max-w-none" data-id="element-173">
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-174">
              Te dijeron un número. Lo anotaste. <br data-id="element-175" />
              <span className="text-red-700 italic" data-id="element-176">Y luego cambió.</span>
            </motion.h2>

            <motion.div variants={itemVariants} className="bg-white p-8 rounded-2xl shadow-sm border border-cream-200 mb-12" data-id="element-177">
              <p className="lead text-xl font-medium mb-6" data-id="element-178">
                Incluso ya te viste con las llaves en la mano. Y luego… a días
                del cierre… el número cambió.
              </p>
              <p className="mb-4" data-id="element-179">
                No porque seas mala con el dinero. No porque “no entendiste”.
              </p>
              <p data-id="element-180">
                Cambió porque comprar casa en Estados Unidos tiene una trampa:
                la mayoría de costos importantes llegan disfrazados de estimados
                hasta que alguien, tarde, confirma taxes, seguro, HOA,
                prorrateos y fechas.
              </p>
            </motion.div>
          </div>

          {/* Pain Points Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-181">
            <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-red-500" data-id="element-182">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4 text-red-600" data-id="element-183">
                <DollarSign className="w-6 h-6" data-id="element-184" />
              </div>
              <h3 className="font-bold text-lg mb-2" data-id="element-185">El Lender</h3>
              <p className="text-navy-800/70 text-sm" data-id="element-186">
                Te da una cifra "aproximada" que rara vez coincide con la final.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-orange-500" data-id="element-187">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4 text-orange-600" data-id="element-188">
                <AlertTriangle className="w-6 h-6" data-id="element-189" />
              </div>
              <h3 className="font-bold text-lg mb-2" data-id="element-190">El Realtor</h3>
              <p className="text-navy-800/70 text-sm" data-id="element-191">
                Te dice "es normal" mientras tú sientes que te están emboscando.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-navy-500" data-id="element-192">
              <div className="w-12 h-12 bg-navy-100 rounded-full flex items-center justify-center mb-4 text-navy-600" data-id="element-193">
                <FileText className="w-6 h-6" data-id="element-194" />
              </div>
              <h3 className="font-bold text-lg mb-2" data-id="element-195">Tú</h3>
              <p className="text-navy-800/70 text-sm" data-id="element-196">
                Solo quieres saber: ¿cuánto dinero real necesito para cerrar?
              </p>
            </motion.div>
          </div>

          {/* The Real Problem */}
          <motion.div variants={itemVariants} className="text-center max-w-2xl mx-auto pt-8" data-id="element-197">
            <h3 className="text-2xl font-serif font-bold mb-4" data-id="element-198">
              El problema no es que te falte información
            </h3>
            <p className="text-lg text-navy-800/80 mb-8" data-id="element-199">
              El problema es que tienes información desordenada. Closing costs,
              prepaids, escrows, credits... como si tu cerebro tuviera que armar
              el rompecabezas en medio del estrés.
            </p>
          </motion.div>

          {/* Costly Mistakes Sub-section (NEW) */}
          <motion.div variants={itemVariants} className="bg-cream-50 border border-orange-200 rounded-2xl p-8 max-w-3xl mx-auto" data-id="element-200">
            <h4 className="text-xl font-bold text-navy-900 mb-6 flex items-center justify-center" data-id="element-201">
              <AlertCircle className="w-6 h-6 text-orange-500 mr-2" data-id="element-202" />Y ahí es
              cuando la gente comete errores caros:
            </h4>
            <div className="grid gap-4" data-id="element-203">
              {mistakes.map((mistake, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: idx * 0.1
            }} className="flex items-start bg-white p-4 rounded-lg shadow-sm" data-id="element-204">
                  <div className="bg-orange-100 p-1.5 rounded-full mr-3 mt-0.5" data-id="element-205">
                    <AlertTriangle className="w-4 h-4 text-orange-600" data-id="element-206" />
                  </div>
                  <span className="text-navy-800 font-medium" data-id="element-207">{mistake}</span>
                </motion.div>)}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}